import {GetOneUserCommand, GetTeamCommand, type PublicUser, type Team} from '@perfectpost/perfect-post-common';
import {useQuery} from '@tanstack/react-query';

import {sendOrThrow} from '../../../services/sendOrThrow';
import {teamKeys} from '../keys';

export interface TeamsQueryResult {
  teams: Team[];
  members: PublicUser[];
}

/**
 * Les équipes de l'utilisateur, et les fiches publiques de leurs membres.
 *
 * Remplace le thunk `fetchTeams` de `src/store/teamslice.ts`. Il était dispatché
 * depuis cinq endroits — au montage de la session, après une invitation, après
 * un retour de paiement Stripe, après un changement de réglages — chacun pour
 * s'assurer que la liste était à jour. Les quatre derniers deviennent des
 * invalidations, portées par les mutations elles-mêmes.
 *
 * La résolution des membres en N+1 requêtes (une par identifiant distinct) est
 * reprise telle quelle : c'est un contrat de l'API, pas un choix de Redux.
 */
export function useTeamsQuery() {
  return useQuery({
    queryKey: teamKeys.list(),
    queryFn: async (): Promise<TeamsQueryResult> => {
      const teams = await sendOrThrow(new GetTeamCommand());
      const memberIds = [...new Set(teams.flatMap((team) => team.users.map((u) => u.user)))];
      const members = await Promise.all(memberIds.map((userId) => sendOrThrow(new GetOneUserCommand({id: userId}))));
      return {teams, members};
    },
  });
}
